import { Popover } from "~/components/ui";
import { type FunctionComponent, type KeyboardEvent, type MouseEvent, useCallback } from "react";
import type { UsedSource } from "@noa/api-core";
import { isApiUrl, openDocument } from "../../../utils/document-url.utils";
import { SourceInfo } from "./source-info";

interface LinkDocInfoPopoverProps {
  used_sources: UsedSource;
  children: React.ReactNode;
  href?: string;
}

export const LinkDocInfoPopover: FunctionComponent<LinkDocInfoPopoverProps> = ({ used_sources, children, href }) => {
  const popoverContent = <SourceInfo used_sources={used_sources} />;

  const hasExternalLink = href && !href?.startsWith("#");
  const requiresAuth = hasExternalLink && isApiUrl(href);

  const navigateToLink = useCallback(() => {
    if (!href || !hasExternalLink) return;

    if (requiresAuth) {
      openDocument(href);
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  }, [href, hasExternalLink, requiresAuth]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();
      navigateToLink();
    },
    [navigateToLink]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLAnchorElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        navigateToLink();
      }
    },
    [navigateToLink]
  );

  return (
    <Popover content={popoverContent} title={used_sources.source_document_name} placement="top" nativeButton={false}>
      <a
        href={href}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{ cursor: hasExternalLink ? "pointer" : "default", textDecoration: "inherit", color: "inherit" }}
      >
        {children}
      </a>
    </Popover>
  );
};
